import React from "react";
import * as api from "../../services/api";
import { useLatest } from "../../hooks";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FilePreview } from "../File";
import * as f from "../../utils/formatter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImagePortrait,
  faScannerGun,
  faTeeth,
} from "@fortawesome/pro-solid-svg-icons";
import * as fe from "../FileExport";
import { Selectable, useExportState } from "../FileExport";
import { useTranslation } from "react-i18next";
import { parseUtcDateTime } from "shared/src/date.mjs";

export const FILE_COLLECTION_TYPES = {
  PHOTO: "Fotografie",
  RTG: "RTG",
  CT: "CT",
};

const getIcon = (fileCollection) => {
  if (fileCollection.class_name === FILE_COLLECTION_TYPES.RTG)
    return faScannerGun;
  else if (fileCollection.class_name === FILE_COLLECTION_TYPES.CT)
    return faTeeth;
  return faImagePortrait;
};

function useFileCollections(client_id) {
  const [state, setState] = React.useState({
    loading: true,
    fileCollections: [],
  });

  useLatest(
    async (signal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const result = await api.loadClientGalleryFileCollections(
          { client_id },
          signal,
        );

        setState((state) => ({
          ...state,
          fileCollections: result.data?.client?.fileCollections ?? [],
        }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [client_id, setState],
  );

  return state;
}

function Document({ client_id, fileCollection, exportState, setExportState }) {
  return (
    <TimelineItem
      sx={{
        display: "flex",
        alignItems: "stretch",
        minHeight: "120px",
      }}
    >
      <TimelineOppositeContent
        sx={{ textAlign: "left", flex: 0.3, pr: 4, pb: 5, maxWidth: "185px" }}
      >
        <Typography
          variant="h3"
          sx={{
            color: "primary.main",
            fontSize: "1.6rem",
          }}
        >
          {fileCollection.class_name}
        </Typography>
        <Typography
          variant="h3"
          sx={(theme) => ({
            color: theme.palette.secondary.main,
            mt: 1,
            fontSize: "1.3rem",
          })}
        >
          {f.shortDateTime(parseUtcDateTime(fileCollection.created_at))}
        </Typography>
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineDot
          sx={{
            boxShadow: "none",
            aspectRatio: "1",
            padding: "8px",
            color: "primary.main",
            backgroundColor: "divider",
            fontSize: "1.8rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon icon={getIcon(fileCollection)} />
        </TimelineDot>

        <TimelineConnector sx={{ backgroundColor: "grey.300", width: "1px" }} />
        <TimelineDot
          sx={{
            padding: "1px",
            marginTop: 0,
            boxShadow: "none",
            alignSelf: "center",
            color: "grey.300",
          }}
        />
      </TimelineSeparator>

      <TimelineContent sx={{ pl: 3, paddingBottom: 5 }}>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {fileCollection.files.map((f) => {
            const fileInfo = {
              type: fileCollection.type,
              objectid: fileCollection.id,
              file: f.id,
            };

            return (
              <Selectable
                key={f.id}
                enabled={exportState.active && !exportState.loading}
                selected={fe.isSelected(exportState, fileInfo)}
                onSelect={() =>
                  setExportState((state) => fe.toggleSelection(state, fileInfo))
                }
              >
                <FilePreview
                  key={f.id}
                  client_id={client_id}
                  fileInfo={fileInfo}
                  fileCollection={fileCollection}
                  readOnly={exportState.active}
                />
              </Selectable>
            );
          })}
        </Box>
      </TimelineContent>
    </TimelineItem>
  );
}

function Content({ client_id, fileCollections }) {
  const [exportState, setExportState] = useExportState();
  const { t } = useTranslation();

  return (
    <>
      <Box display="flex" justifyContent="space-between" sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h1">
          {t("client.gallery.title", "Galerie")}
        </Typography>
        <fe.ExportButton
          client_id={client_id}
          exportState={exportState}
          setExportState={setExportState}
        />
      </Box>
      <Timeline>
        {fileCollections.map((fc) => (
          <Document
            key={`${fc.type}.${fc.id}`}
            client_id={client_id}
            fileCollection={fc}
            setExportState={setExportState}
            exportState={exportState}
          />
        ))}
      </Timeline>
    </>
  );
}

export function Gallery({ client_id }) {
  const { fileCollections, loading } = useFileCollections(client_id);

  if (loading) {
    return <CircularProgress />;
  }

  return <Content client_id={client_id} fileCollections={fileCollections} />;
}
